import { selectBoxWithoutBorderStyle } from '@common/styled';
import { ViewType } from '@LSP/types';
import { negateVariable, theme } from '@prophecy/theme';
import { Stack, Select } from '@prophecy/ui-v3';
import { Alert, AlertProps } from '@prophecy/ui-v3/ui-compat';
import { subtractDays, subtractHours, subTractMinutes, subtractMonths, subtractYears } from '@prophecy/utils/date';
import styled from 'styled-components';

import { ExecutionStatus } from '../../../data/apis/types';
import { StyledButton } from '../../Workflow/JobBanner';
import { ResetObservationKeyWord } from '../redux/types';

const StyledReadOnlyBanner = styled(Alert)<{ viewType?: ViewType }>`
  ${(props) =>
    props.viewType === ViewType.visual &&
    `
    margin-top: ${negateVariable(theme.spaces.x12)};
  `}
  padding: ${theme.spaces.x8} ${theme.spaces.x8}  ${theme.spaces.x8}  ${theme.spaces.x12};
  height: 44px;
  border-color: ${theme.colors.brand300};
  align-items: center;
`;

const StyledStatusSelect = styled(Select)`
  width: 200px;
  display: none;
`;

const StyledSelect = styled(Select)`
  width: 210px;
  ${selectBoxWithoutBorderStyle}
`;

type BannerProps = {
  handleDateChange: (value?: number | string) => void;
  loading?: boolean;
  startDateTo: Date;
  handleStatusChange?: (value?: string) => void;
  status?: string;
  startDateFrom?: number;
};

const getDateOptions = (today: Date) => {
  return [
    {
      label: 'Last 5 minutes',
      value: subTractMinutes(today, 5).getTime()
    },
    {
      label: 'Last 15 minutes',
      value: subTractMinutes(today, 15).getTime()
    },
    {
      label: 'Last 30 minutes',
      value: subTractMinutes(today, 30).getTime()
    },
    {
      label: 'Last 1 hour',
      value: subtractHours(1, today).getTime()
    },
    {
      label: 'Last 3 hours',
      value: subtractHours(3, today).getTime()
    },
    {
      label: 'Last 6 hours',
      value: subtractHours(6, today).getTime()
    },
    {
      label: 'Last 12 hours',
      value: subtractHours(12, today).getTime()
    },
    {
      label: 'Last 24 hours',
      value: subtractHours(24, today).getTime()
    },
    {
      label: 'Last 2 days',
      value: subtractDays(2, today).getTime()
    },
    {
      label: 'Last 7 days',
      value: subtractDays(7, today).getTime()
    },
    {
      label: 'Last 15 days',
      value: subtractDays(15, today).getTime()
    },
    {
      label: 'Last 1 month',
      value: subtractMonths(today, 1).getTime()
    },
    {
      label: 'Last 3 months',
      value: subtractMonths(today, 3).getTime()
    },
    {
      label: 'Last 6 months',
      value: subtractMonths(today, 6).getTime()
    },
    {
      label: 'Last 9 months',
      value: subtractMonths(today, 9).getTime()
    },
    {
      label: 'Last 1 year',
      value: subtractYears(today, 1).getTime()
    },
    {
      label: 'Last 2 years',
      value: subtractYears(today, 2).getTime()
    },
    {
      label: 'Last 3 years',
      value: subtractYears(today, 3).getTime()
    }
  ];
};

export function ReadOnlyBanner({
  children: _children,
  variant: _variant,
  handleDateChange,
  loading,
  status,
  handleStatusChange = () => {},
  startDateFrom,
  startDateTo,
  ...restProps
}: AlertProps & BannerProps) {
  const dateOptions = getDateOptions(startDateTo);

  return (
    <StyledReadOnlyBanner variant='info' showIcon={false} banner={true} {...restProps}>
      <Stack direction='horizontal' alignY='center' align='space-between' width='60%'>
        <span>View (Read-only)</span>
        <StyledSelect
          size='s'
          placeholder='Observation Date..'
          value={startDateFrom ? startDateFrom : 'Observation Date..'}
          options={dateOptions}
          disabled={loading}
          aria-label='Select observation date'
          data-test-id='observation-readonly-date-select'
          onChange={handleDateChange}
          footer={
            <Stack width='100%' direction='horizontal' alignY='center'>
              {startDateFrom && (
                <StyledButton
                  variant='tertiaryGray'
                  block={true}
                  size='l'
                  onClick={() => handleDateChange(ResetObservationKeyWord)}
                  data-test-id='observation-date-reset-button'>
                  Reset
                </StyledButton>
              )}
            </Stack>
          }
        />
        <StyledStatusSelect
          size='s'
          placeholder='Status..'
          value={status ? status : 'Status...'}
          options={Object.keys(ExecutionStatus).map((value) => ({ label: value, value }))}
          disabled={loading}
          onChange={handleStatusChange}
          aria-label='Select status'
          data-test-id='readonly-banner-status-select'
          footer={
            <Stack width='100%' direction='horizontal' alignY='center'>
              {status && (
                <StyledButton
                  variant='tertiaryGray'
                  block={true}
                  size='l'
                  onClick={() => handleStatusChange(undefined)}
                  data-test-id='observation-status-reset-button'>
                  Reset
                </StyledButton>
              )}
            </Stack>
          }
        />
      </Stack>
    </StyledReadOnlyBanner>
  );
}
